/**
 * Supabase Client Configuration for MARS-X
 * 
 * This replaces/augments the Dexie.js local database with cloud sync
 * 
 * Security History:
 * - Remediation 2025-10-003: Memory-based SecureSessionStorage (XSS-resistant)
 * - Sprint 2025-11-011: Encrypted localStorage with session timeout (UX improvement)
 * 
 * Current Strategy (Sprint 2025-11-011):
 * - Session storage uses EncryptedSessionStorage
 * - Tokens encrypted with AES-GCM-256 before localStorage
 * - Automatic 30-minute session timeout (7 days for "Remember Me")
 * - Device fingerprinting for hijacking detection
 * - Sessions survive page reload (user experience)
 * - Defense-in-depth: CSP + Encryption + Timeout + Fingerprint
 */

import { createClient } from '@supabase/supabase-js';
import { createLogger } from './utils/logger';
import { encryptedStorage } from './auth/encryptedStorage';

const logger = createLogger('Supabase');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Create a dummy Supabase client if credentials aren't configured (for offline mode)
// Use placeholder values that won't cause errors but won't actually connect
const isConfigured = !!(supabaseUrl && supabaseAnonKey);

if (!isConfigured) {
  logger.warn('Supabase credentials not configured - running in offline-only mode');
}

// Create Supabase client with valid URLs (use placeholder if not configured)
// The client will fail gracefully when making actual requests
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: encryptedStorage, // Sprint 2025-11-011: Encrypted localStorage with timeout
  },
  db: {
    schema: 'public',
  },
  global: {
    headers: {
      'X-Client-Info': 'mars-x@0.5.0',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10, // Bandwidth optimization
    },
  },
});

// Helper: Check connection
export async function checkSupabaseConnection(): Promise<boolean> {
  if (!supabaseUrl || !supabaseAnonKey) return false;
  
  try {
    const { error } = await supabase.from('organizations').select('count').limit(1);
    if (error) {
      logger.error('Supabase connection error', error);
      return false;
    }
    logger.info('Supabase connected successfully');
    return true;
  } catch (error) {
    logger.error('Supabase connection failed', error);
    return false;
  }
}

// Helper: Get current user
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) {
    logger.error('Error getting user', error);
    return null;
  }
  return user;
}

// Helper: Sign out
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

logger.info('Supabase client initialized', { 
  configured: !!(supabaseUrl && supabaseAnonKey),
  url: supabaseUrl || 'Not configured' 
});

